import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, PhoneOff, Bell, Sparkles, Check, ChevronRight } from "lucide-react";

const chips = [
  { label: "15 min", i: 0 },
  { label: "30 min", i: 1 },
  { label: "1 hr", i: 2 },
  { label: "Tomorrow 9 AM", i: 3 },
];

const transcript = [
  "Follow up with",
  " Alex",
  " on Saturday",
  " at 9 AM",
  " about the tile",
  " quotation.",
];

export default function VoiceReminder() {
  return (
    <section id="voice-reminder" className="relative py-24 md:py-40 border-t border-black/5" data-testid="voice-reminder-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6 md:order-2">
          <VoiceReminderDevice />
        </div>

        <div className="md:col-span-6 md:order-1">
          <div className="overline mb-4">Flagship feature</div>
          <h2 className="font-display text-4xl md:text-6xl font-medium leading-[0.95] tracking-tight text-[color:var(--ink)] max-w-xl">
            The moment the call ends,
            <span className="gradient-text" style={{ fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
              {" "}
              memory begins.
            </span>
          </h2>
          <p className="mt-6 text-[color:var(--ink-soft)] text-lg leading-relaxed max-w-lg">
            Every time you hang up, BizBinder pops in with a note window. Type it, tap a quick chip
            like <em>&ldquo;Tomorrow 9 AM&rdquo;</em>, or just hold the mic and speak — the assistant transcribes what
            the call was about, when to remind you, and why.
          </p>

          <div className="mt-10 grid gap-5 max-w-md">
            {[
              { icon: PhoneOff, title: "Call-ended trigger", desc: "The note window opens automatically when you hang up." },
              { icon: Mic, title: "Voice-first capture", desc: "Hold the mic and talk. We extract the who, what, and when." },
              { icon: Bell, title: "Smart reminders", desc: "One-tap chips for 15 min, 30 min, 1 hr, or tomorrow at 9 AM." },
            ].map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="flex gap-4 items-start"
                data-testid={`voice-feature-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[color:var(--blue-soft)] flex items-center justify-center shrink-0">
                  <it.icon className="w-4 h-4 text-[color:var(--blue)]" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-[color:var(--ink)] font-medium">{it.title}</div>
                  <div className="text-[color:var(--ink-soft)] text-sm mt-0.5">{it.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function VoiceReminderDevice() {
  const [step, setStep] = useState(0);
  // step: 0 = call ended screen, 1 = mic listening + transcript, 2 = reminder set

  useEffect(() => {
    let mounted = true;
    const cycle = async () => {
      while (mounted) {
        setStep(0);
        await wait(1600);
        setStep(1);
        await wait(3400);
        setStep(2);
        await wait(2600);
      }
    };
    cycle();
    return () => { mounted = false; };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative mx-auto w-[300px] md:w-[340px]"
      data-testid="voice-reminder-device"
    >
      <div
        className="absolute -inset-16 rounded-full opacity-70 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 40%, rgba(30,58,196,0.28), rgba(232,90,42,0.22) 45%, transparent 70%)",
        }}
      />
      <div className="device-frame relative">
        <div className="device-screen bg-[#f4f3fb]">
          <div className="w-full h-full flex flex-col relative">
            {/* Status bar */}
            <div className="flex justify-between items-center px-6 pt-4 text-[10px] text-[color:var(--ink)]/70">
              <span className="font-medium">9:41</span>
              <span />
              <span>◉</span>
            </div>

            <AnimatePresence mode="wait">
              {step === 0 && <CallEndedScreen key="ended" />}
              {step === 1 && <VoiceCaptureScreen key="voice" />}
              {step === 2 && <ReminderSetScreen key="set" />}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Floating callout — active step label */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="absolute -right-4 md:-right-14 top-6 rounded-2xl border border-[color:var(--line)] bg-white/95 backdrop-blur-xl px-4 py-3 text-xs shadow-2xl z-40"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[color:var(--accent)]" />
          <span className="text-[color:var(--ink)] font-medium">
            {step === 0 ? "Call just ended" : step === 1 ? "Listening…" : "Reminder saved"}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

function wait(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function CallEndedScreen() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="flex-1 flex flex-col relative overflow-hidden"
    >
      {/* Dimmed background (like real bottom sheet) */}
      <div className="absolute inset-0 bg-black/45" />

      {/* Bottom sheet */}
      <motion.div
        initial={{ y: 40 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.2, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="mt-auto relative bg-white rounded-t-3xl px-5 pt-5 pb-4"
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-black/15 mb-3" />
        <div className="text-[color:var(--ink)] font-display text-base font-semibold leading-tight">
          Add call details?
        </div>
        <div className="text-[color:var(--ink-mute)] text-xs mt-0.5">Alex Bennett</div>

        <div className="mt-3 h-px bg-[color:var(--line)]" />

        {/* NOTE */}
        <div className="mt-3 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] font-medium">Note</div>
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="w-8 h-8 rounded-full bg-[color:var(--blue-soft)] flex items-center justify-center"
          >
            <Mic className="w-3.5 h-3.5 text-[color:var(--blue)]" strokeWidth={2} />
          </motion.div>
        </div>

        <div className="mt-2 rounded-xl bg-[color:var(--surface-2)] p-3">
          <div className="text-[color:var(--ink)] text-[11px] leading-snug">
            Alex loved the 200-unit tile order and wants a second quote by Saturday.
          </div>
          <div className="mt-1.5 text-[color:var(--ink)] text-[11px] leading-snug">
            • Call Alex Saturday 9 AM to confirm shipping timeline.
          </div>
        </div>

        <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[color:var(--accent-soft)] text-[color:var(--accent)] text-[9px] font-medium">
          <Sparkles className="w-2.5 h-2.5" />
          AI-filled from your voice · tap to edit
        </div>

        {/* REMINDER header + toggle */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] font-medium">Reminder</div>
          <div className="w-9 h-5 rounded-full bg-[color:var(--blue)] p-0.5 flex justify-end">
            <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
          </div>
        </div>

        <div className="mt-2 rounded-xl bg-[color:var(--surface-2)] px-3 py-2.5 text-[10.5px] text-[color:var(--ink)]">
          Call Alex Bennett to send a new quotation
        </div>

        {/* Chip row */}
        <div className="mt-2 flex gap-1.5">
          {chips.map((c, i) => (
            <div
              key={c.label}
              className={`px-2 py-1 rounded-full text-[9px] border ${
                i === 3
                  ? "bg-[color:var(--blue-soft)] text-[color:var(--blue)] border-transparent font-medium"
                  : "bg-white text-[color:var(--ink)]/70 border-[color:var(--line)]"
              }`}
            >
              {c.label}
            </div>
          ))}
        </div>

        {/* Calendar row */}
        <div className="mt-2 rounded-xl bg-white border border-[color:var(--line)] px-3 py-2 flex items-center gap-2">
          <div className="w-5 h-5 rounded-md bg-[color:var(--blue-soft)] flex items-center justify-center">
            <Bell className="w-2.5 h-2.5 text-[color:var(--blue)]" strokeWidth={2} />
          </div>
          <div className="text-[color:var(--ink)] text-[10px] font-medium">Sat, Aug 1, 2026 · 9:00 AM</div>
          <ChevronRight className="w-3 h-3 text-[color:var(--ink-mute)] ml-auto" />
        </div>

        {/* Save + Skip */}
        <button className="mt-3 w-full h-10 rounded-xl bg-[color:var(--blue)] text-white text-[11px] font-semibold">
          Save
        </button>
        <div className="mt-2 text-center text-[color:var(--ink-mute)] text-[10px]">Skip</div>
      </motion.div>
    </motion.div>
  );
}

function VoiceCaptureScreen() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="flex-1 flex flex-col px-5 pt-6"
    >
      <div className="text-[10px] text-[color:var(--ink-mute)] uppercase tracking-widest">Listening · Alex Bennett</div>

      <div className="mt-4 rounded-2xl border border-[color:var(--blue)]/30 bg-[color:var(--blue-soft)] p-4">
        <div className="text-[10px] text-[color:var(--blue)] uppercase tracking-widest font-medium">Live transcript</div>
        <div className="mt-2 text-[color:var(--ink)] text-sm leading-relaxed min-h-[68px]">
          {transcript.map((w, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 + i * 0.35, duration: 0.25 }}
            >
              {w}
            </motion.span>
          ))}
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ delay: 2.4, duration: 0.8, repeat: Infinity }}
            className="inline-block w-[2px] h-4 bg-[color:var(--ink)] align-middle ml-0.5"
          />
        </div>
      </div>

      {/* Waveform */}
      <div className="mt-4 flex items-end justify-center gap-1 h-16">
        {Array.from({ length: 26 }).map((_, i) => (
          <motion.div
            key={i}
            className="w-1 rounded-full"
            style={{
              background:
                i % 3 === 0 ? "var(--accent)" : "var(--blue)",
            }}
            animate={{
              height: [
                8 + ((i * 7) % 16),
                14 + ((i * 11) % 34),
                6 + ((i * 5) % 20),
              ],
            }}
            transition={{
              duration: 0.9 + (i % 4) * 0.15,
              repeat: Infinity,
              delay: (i % 5) * 0.05,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="mt-4 rounded-xl border border-dashed border-[color:var(--line-strong)] p-3">
        <div className="text-[10px] text-[color:var(--ink-mute)] uppercase tracking-widest">Detected reminder</div>
        <div className="mt-1 flex items-center gap-2 text-[color:var(--ink)] text-sm">
          <Bell className="w-3.5 h-3.5 text-[color:var(--accent)]" />
          Tomorrow, 9:00 AM
        </div>
      </div>

      <div className="mt-auto pb-6 flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.14, 1] }}
          transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          <div className="w-16 h-16 rounded-full bg-[color:var(--accent)] flex items-center justify-center shadow-xl shadow-[color:var(--accent)]/30">
            <Mic className="w-6 h-6 text-white" />
          </div>
          <div className="absolute inset-0 rounded-full border-2 border-[color:var(--accent)]/50 animate-ping" />
        </motion.div>
      </div>
    </motion.div>
  );
}

function ReminderSetScreen() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="flex-1 flex flex-col px-5 pt-6"
    >
      <div className="text-[10px] text-[color:var(--ink-mute)] uppercase tracking-widest">Saved</div>

      <div className="mt-4 rounded-2xl border border-[color:var(--line)] p-4 bg-white shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[color:var(--ink)] font-medium">Alex Bennett</div>
            <div className="text-[color:var(--ink-mute)] text-xs mt-0.5">CEO · Kingdom Trading Co.</div>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 300, damping: 18 }}
            className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center"
          >
            <Check className="w-4 h-4" strokeWidth={3} />
          </motion.div>
        </div>
        <div className="mt-4 text-[color:var(--ink)] text-sm leading-relaxed">
          Follow up with Rohit tomorrow at 9 AM about the ceramic tile quote.
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-[color:var(--accent-soft)] p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
          <Bell className="w-4 h-4 text-[color:var(--accent)]" />
        </div>
        <div>
          <div className="text-[color:var(--ink)] text-sm font-medium">Reminder set</div>
          <div className="text-[color:var(--ink-soft)] text-xs">Tomorrow · 9:00 AM · Notification + Push</div>
        </div>
      </div>

      <div className="mt-auto pb-6 grid grid-cols-2 gap-2">
        <button className="h-11 rounded-full border border-[color:var(--line-strong)] text-[color:var(--ink)] text-xs font-medium">Edit note</button>
        <button className="h-11 rounded-full bg-[color:var(--ink)] text-white text-xs font-medium">Done</button>
      </div>
    </motion.div>
  );
}
